"use client";

import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslations } from "next-intl";
import { ProfileOutlined } from "@ant-design/icons";
import BlogSearchTip from "@/components/Blogs/BlogSearchTip";
import { Blog } from "@/lib/@types/api/blog";
import { Link } from "@/navigation.ts";
import CustomImage from "../Image";
import moment from "moment";

// 使用 TailwindCSS 的一些自定义样式来优化文章显示
export const StyledArticle = styled.article`
	/* 定义与全局无关的样式 */
	.blog-content {
		h1,
		h2,
		h3,
		h4,
		h5,
		h6 {
			scroll-margin-top: 115px;
			margin: 1.5rem 0 1rem;
			line-height: 1.35;
		}

		p {
			display: block;
			margin: 1rem 0;
			line-height: 1.75;
			unicode-bidi: isolate;
		}

		ul,
		ol {
			margin: 1rem 0;
			padding-left: 1.25rem;
		}

		ul {
			list-style-type: disc;
		}

		ol {
			list-style-type: decimal;
		}

		li {
			margin: 0.5rem 0;
		}

		strong {
			padding: 0 2px;
		}

		h1 {
			color: #18181b;
			font-weight: 700;
			font-size: 28px;
			padding: 4px 0;
		}

		h2 {
			color: #18181b;
			font-weight: 700;
			font-size: 24px;
			padding: 4px 0;
		}

		h3 {
			color: #18181b;
			font-weight: 700;
			font-size: 20px;
			padding: 4px 0;
		}

		h4 {
			color: #18181b;
			font-weight: 600;
			font-size: 16px;
			padding: 4px 0;
		}

		h5 {
			color: #18181b;
			font-weight: 600;
			font-size: 14px;
			padding: 4px 0;
		}

		h6 {
			color: #18181b;
			font-weight: 500;
			font-size: 13px;
			padding: 4px 0;
		}

		span {
			font-size: unset;
			padding: 4px 0;
		}

		/* 表格内文字不再额外撑开段落间距 */
		table p {
			margin: 0;
		}

		a {
			padding: 0 2px;
		}

		blockquote {
			border-left: 2px solid #ccc;
			margin: 1.25rem 0 1.25rem 24px;
			padding-left: 16px;
		}

		table {
			display: inline-block;
			border: none;
		}

		.wrap-left {
			float: left;
			margin: 0 1rem 1rem 0;
		}

		.wrap-right {
			float: right;
			margin: 0 0 1rem 1rem;
		}

		.no-wrap {
			float: none;
			margin: 0;
		}

		th,
		td {
			border: 1px solid #dcdcdc;
			padding: 8px;
			text-align: left;
		}
	}
`;

// 定义标题项类型
interface HeadingItem {
	text: string;
	level: number;
	id: string;
	number: string;
}

type HeadingSeed = Omit<HeadingItem, "number">;

const normalizeHeadingText = (text: string) => text.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();

const createHeadingId = (text: string, usedIds: Map<string, number>) => {
	const baseId = text.toLowerCase().replace(/\s+/g, "-");
	const duplicateCount = usedIds.get(baseId) || 0;

	usedIds.set(baseId, duplicateCount + 1);

	return duplicateCount === 0 ? baseId : `${baseId}-${duplicateCount + 1}`;
};

// 生成层级序号的函数
const generateHierarchicalNumbers = (
	headings: HeadingSeed[],
): HeadingItem[] => {
	const counters = [0, 0, 0, 0, 0]; // 对应 h1, h2, h3, h4, h5 的计数器

	return headings.map((heading) => {
		const levelIndex = heading.level - 1; // h1=0, h2=1, h3=2, h4=3, h5=4

		// 增加当前层级的计数器
		counters[levelIndex]++;

		// 重置更深层级的计数器
		for (let i = levelIndex + 1; i < counters.length; i++) {
			counters[i] = 0;
		}

		// 生成序号字符串
		const numberParts = counters.slice(0, levelIndex + 1).filter((count) => count > 0);
		const number = numberParts.join(".");

		return {
			...heading,
			number,
		};
	});
};

const BlogArticle = ({ content, blogList }: { content: string; blogList: Blog.BlogListItem[] }) => {
	const [headings, setHeadings] = useState<HeadingItem[]>([]);
	const [newContent, setNewContent] = useState<string>(content); // 用于存储修改后的内容
	const [innerHeight, setInnerHeight] = useState<number>(1000); // 用于存储窗口高度

	// 提取文章的 H1-H5 标题并生成目录，同时更新 newContent
	useEffect(() => {
		const parser = new DOMParser();

		const doc = parser.parseFromString(content, "text/html");
		const usedHeadingIds = new Map<string, number>();

		// 提取标题并生成 ID，只把真正有内容的标题加入目录
		const foundHeadings = Array.from(doc.querySelectorAll("h1, h2, h3, h4, h5")).reduce<HeadingSeed[]>((acc, h) => {
			const headingText = normalizeHeadingText(h.textContent || "");

			if (!headingText) {
				return acc;
			}

			const headingId = createHeadingId(headingText, usedHeadingIds);
			const level = parseInt(h.tagName.substring(1), 10); // 获取标题层级 (1-5)

			h.id = headingId; // 给每个有效标题元素设置 ID
			acc.push({
				text: headingText,
				level,
				id: headingId,
			});

			return acc;
		}, []);

		// 生成层级序号
		const hierarchicalHeadings = generateHierarchicalNumbers(foundHeadings);

		// 更新内容为带有 ID 的 HTML
		// 使用 body.innerHTML 而不是 XMLSerializer，原因：
		// 1. XMLSerializer().serializeToString(doc) 会返回完整的文档结构（包括 <html><head><body> 等标签）
		// 2. 而 dangerouslySetInnerHTML 只需要 body 内的内容，不需要外层标签
		// 3. body.innerHTML 能更好地保持原有 HTML 结构，避免破坏嵌套元素（如链接内的 strong 标签）
		const updatedContent = doc.body?.innerHTML || doc.documentElement?.innerHTML || content;
		setNewContent(updatedContent);
		setHeadings(hierarchicalHeadings); // 更新目录
	}, [content]);

	useEffect(() => {
		setInnerHeight(window.innerHeight); // 获取当前窗口高度
	}, []);

	const t = useTranslations();
	return (
		<div className="relative flex justify-between gap-x-5 py-10 max-md:flex-wrap">
			{/* 左侧文章内容 */}
			<StyledArticle
				className="animate__animated animate__fadeIn h-[100vh] flex-1 overflow-auto overflow-y-auto pr-[460px] max-md:pr-0"
				style={{
					scrollbarWidth: "none", // Firefox
					msOverflowStyle: "none", // IE 10+
					minHeight: innerHeight < 1000 ? "1200px" : "auto", // 当屏幕高度小于1000时设置最小高度，使右侧的目录不至于挤压下方内容
				}}
			>
				{/* 渲染修改后的内容，并通过 dangerouslySetInnerHTML 来处理 */}
				<div
					className="blog-content text-base"
					dangerouslySetInnerHTML={{
						__html: newContent, // 渲染修改后的内容
					}}
				/>
			</StyledArticle>

			{/* 右侧 Table of Contents (TOC) */}
			<div className={`absolute right-0 w-[440px] max-md:hidden`}>
				{/* 目录导航 */}
				<nav className="border-gray-450 my-4 rounded border bg-[#f9f9f9] p-4 flex flex-col">
					{/* 固定标题部分 */}
					<div className="mb-4 flex items-center justify-between flex-shrink-0">
						<h2 className="text-xl font-semibold">{t("blog.Contents")}</h2>
						<ProfileOutlined className="rounded-xl text-2xl text-gray-300" />
					</div>
					{/* 可滚动的内容部分 */}
					<ul 
						className="space-y-2 overflow-y-auto flex-1 blog-toc-scrollbar"
						style={{ maxHeight: "450px" }}
					>
						{headings.map((heading, index) => (
							<li
								key={index}
								className="cursor-pointer text-sm"
								style={{ paddingLeft: `${(heading.level - 1) * 16}px` }} // 根据层级添加缩进
							>
								<span className="mr-2 font-medium text-gray-600">{heading.number}</span>
								<a className="hover:underline" href={`#${heading.id}`}>
									{heading.text}
								</a>
							</li>
						))}
					</ul>
				</nav>
				<style dangerouslySetInnerHTML={{
					__html: `
						.blog-toc-scrollbar {
							scrollbar-width: thin;
							scrollbar-color: #cbd5e1 transparent;
						}
						.blog-toc-scrollbar::-webkit-scrollbar {
							width: 6px;
						}
						.blog-toc-scrollbar::-webkit-scrollbar-track {
							background: transparent;
						}
						.blog-toc-scrollbar::-webkit-scrollbar-thumb {
							background: #cbd5e1;
							border-radius: 10px;
						}
						.blog-toc-scrollbar::-webkit-scrollbar-thumb:hover {
							background: #94a3b8;
						}
						.related-blog-scrollbar {
							scrollbar-width: thin;
							scrollbar-color: #cbd5e1 transparent;
						}
						.related-blog-scrollbar::-webkit-scrollbar {
							width: 6px;
						}
						.related-blog-scrollbar::-webkit-scrollbar-track {
							background: transparent;
						}
						.related-blog-scrollbar::-webkit-scrollbar-thumb {
							background: #cbd5e1;
							border-radius: 10px;
						}
						.related-blog-scrollbar::-webkit-scrollbar-thumb:hover {
							background: #94a3b8;
						}
					`
				}} />

				{/* 搜索组件 */}
				<div className="my-4">
					<BlogSearchTip></BlogSearchTip>
				</div>
				
				<RelatedBlog blogList={blogList}></RelatedBlog>
			</div>
		</div>
	);
};

const RelatedBlog = ({ blogList }: { blogList: Blog.BlogListItem[] }) => {
	const t = useTranslations();

	return (
		<div className="mt-6">
			<h2 className="mb-6 border-b border-gray-300 pb-4 text-xl font-semibold">
				{t("blog.RelatedBlogs")}
			</h2>
			<div className="flex flex-col gap-4 max-h-[400px] overflow-y-auto related-blog-scrollbar">
				{blogList &&
					blogList.map((blog) => {
						return (
							<Link
								key={blog.blog_id}
								href={"/blog/" + blog.blog_slug}
								prefetch={false}
								className="group flex gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-all duration-300 hover:border-mainColor hover:shadow-md flex-shrink-0"
							>
								{/* 左侧图片 */}
								<div className="w-[20%] relative flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
									<img
										src={blog.blog_cover_origin}
										alt={blog.blog_title}
										width={96}
										height={96}
										className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
									/>
								</div>

								{/* 右侧内容 */}
								<div className="w-[80%] flex flex-1 flex-col justify-between min-w-0">
									<div className="flex-1">
										{/* 分类标签 */}
										{blog.blog_classification_list && blog.blog_classification_list.length > 0 && (
											<div className="mb-2">
												{blog.blog_classification_list.slice(0, 1).map((item) => (
													<span
														key={item.cls_slug}
														className="inline-block rounded-full bg-mainColor/10 px-2.5 py-0.5 text-xs font-medium uppercase text-mainColor"
													>
														{item.cls_name}
													</span>
												))}
											</div>
										)}

										{/* 标题 */}
										<h3 className="line-clamp-1 text-sm font-semibold leading-snug text-gray-900 transition-colors duration-300 group-hover:text-mainColor">
											{blog.blog_title}
										</h3>
										
										{blog.blog_excerpt && (
											<div className="text-xs text-gray-600 line-clamp-1 leading-relaxed">
												{blog.blog_excerpt}
											</div>
										)}
									</div>

									{/* 日期 */}
									{blog.blog_upload_time && (
										<div className="mt-1 flex items-center gap-1.5 text-xs text-mainColor">
											<svg
												className="h-3.5 w-3.5 flex-shrink-0"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
												/>
											</svg>
											<span>{moment(blog.blog_upload_time).format("MMM DD, YYYY")}</span>
										</div>
									)}
								</div>
							</Link>
						);
					})}
			</div>
		</div>
	);
};

export default BlogArticle;
